import { createSelector } from "@reduxjs/toolkit";

import type { HandlerDesign, ResponseDesign, TriggerDesign } from "../../schemas/index.ts";
import {
  conditionsStateSelector,
  effectsStateSelector,
  handlersByIdSelector,
  responsesByIdSelector,
  triggerIdsStateSelector,
  triggersByIdSelector,
} from "../selectors/triggerSelectors.ts";
import { getConditionBuildCreate, type GetConditionBuild } from "./conditions/index.ts";
import { queryEffectBuildsCreate, type QueryEffectBuilds } from "./effects/index.ts";

export const getConditionBuildSelector = createSelector([conditionsStateSelector], (conditionsState) =>
  getConditionBuildCreate({ conditionsState }),
);

export const queryEffectBuildsSelector = createSelector([effectsStateSelector], (effectsState) =>
  queryEffectBuildsCreate({ effectsState }),
);

export const getTriggerDesignsSelector = createSelector(
  [
    getConditionBuildSelector,
    queryEffectBuildsSelector,
    triggerIdsStateSelector,
    triggersByIdSelector,
    handlersByIdSelector,
    responsesByIdSelector,
  ],
  (getConditionBuild, queryEffectBuilds, triggerIdsState, triggersById, handlersById, responsesById) =>
    getTriggerDesignsCreate({
      getConditionBuild,
      queryEffectBuilds,
      triggerIdsState,
      triggersById,
      handlersById,
      responsesById,
    }),
);

interface Options {
  getConditionBuild: GetConditionBuild;
  queryEffectBuilds: QueryEffectBuilds;
  triggerIdsState: ReturnType<typeof triggerIdsStateSelector>;
  triggersById: ReturnType<typeof triggersByIdSelector>;
  handlersById: ReturnType<typeof handlersByIdSelector>;
  responsesById: ReturnType<typeof responsesByIdSelector>;
}

export const getTriggerDesignsCreate =
  ({
    getConditionBuild,
    queryEffectBuilds,
    triggerIdsState: { triggerIdsByNodeId, handlerIdsByTriggerId, responseIdsByHandlerId },
    triggersById,
    handlersById,
    responsesById,
  }: Options) =>
  (forNodeId: number): TriggerDesign[] => {
    const triggerIds = triggerIdsByNodeId[forNodeId] ?? [];
    return triggerIds.map((triggerId): TriggerDesign => {
      const { label, cases } = triggersById[triggerId];
      const handlerIds = handlerIdsByTriggerId[triggerId] ?? [];
      return {
        id: triggerId,
        cases,
        label: label || undefined,
        handlers: handlerIds.map((handlerId): HandlerDesign => {
          const handler = handlersById[handlerId];
          const responseIds = responseIdsByHandlerId[handlerId] ?? [];
          return {
            id: handlerId,
            label: handler?.label || undefined,
            cases: handler?.cases,
            responses: responseIds.map((responseId): ResponseDesign => {
              const { label: responseLabel } = responsesById[responseId];
              const effects = queryEffectBuilds({ type: "byResponseId", responseId });
              const condition = getConditionBuild(responseId);
              return {
                id: responseId,
                label: responseLabel || undefined,
                condition,
                effects,
              };
            }),
          };
        }),
      };
    });
  };

export type GetTriggerDesigns = ReturnType<typeof getTriggerDesignsCreate>;
